import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LineRoute, AdminRoute } from './components/auth/AuthMiddleware';
import LiffLogin from './components/line/LiffLogin';
import OneTimeLogin from './components/auth/OneTimeLogin';
import AdminLogin from './components/auth/AdminLogin';
import ManagerLogin from './components/auth/ManagerLogin';
import { PageLoading } from './components/common/LoadingStates';

// Import new layouts (to be created)
import LineLayout from './components/layout/LineLayout';
import AdminLayout from './components/layout/AdminLayout';

// Import placeholder pages (to be created)
import LineDashboard from './pages/line/LineDashboard';
import LineProfile from './pages/line/LineProfile';
import LineClients from './pages/line/LineClients';
import LinePolicies from './pages/line/LinePolicies';
import LineCalendar from './pages/line/LineCalendar';
import LineCoaching from './pages/line/LineCoaching';
import LineTraining from './pages/line/LineTraining';
import LinePerformance from './pages/line/LinePerformance';
import LineTeam from './pages/line/LineTeam';
import LineNotifications from './pages/line/LineNotifications';
import LineTickets from './pages/line/LineTickets';

import AdminDashboard from './pages/admin/AdminDashboard';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminNotificationSettings from './pages/admin/AdminNotificationSettings';
import AdminUsers from './pages/admin/AdminUsers';
import AdminAgents from './pages/admin/AdminAgents';
import AdminClients from './pages/admin/AdminClients';
import AdminPolicies, { 
  AdminCoaching, 
  AdminReports, 
  AdminSettings 
} from './pages/admin/AdminPolicies';
import AdminPerformance from './pages/admin/AdminPerformance';
import AdminCalendar from './pages/admin/AdminCalendar';
import AdminTickets from './pages/admin/AdminTickets';

// Landing page
const LandingPage = () => {
  const contactName = import.meta.env.VITE_ADMIN_CONTRACT_NAME || "Demo AMS Admin";
  const contactPhone = import.meta.env.VITE_ADMIN_CONTACT_PHONE || "+1234567890";
  const contactEmail = import.meta.env.VITE_ADMIN_CONTACT_EMAIL || "admin@demo-ams.com";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-100">
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-8 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">{import.meta.env.VITE_APP_TITLE || 'Demo AMS'}</h1>
          <p className="text-2xl text-gray-600 mb-8">Agent Management System</p>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full"></div>
        </div>

        {/* Main Content Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* What is AMS Card */}
          <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">What is AMS?</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                The <strong>Agent Management System (AMS)</strong> is a comprehensive platform designed to streamline and optimize insurance agent operations, performance tracking, and client management.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Performance Analytics & Reporting</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Client Relationship Management</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Policy Management & Tracking</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Team Collaboration & Coaching</span>
                </div>
              </div>
            </div>
          </div>

          {/* Key Features Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Key Features</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-center space-x-3">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span>Real-time Dashboard</span>
              </li>
              <li className="flex items-center space-x-3">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span>Calendar Integration</span>
              </li>
              <li className="flex items-center space-x-3">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span>Performance Metrics</span>
              </li>
              <li className="flex items-center space-x-3">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span>Team Management</span>
              </li>
              <li className="flex items-center space-x-3">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span>Mobile LINE Integration</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Increased Productivity</h4>
            <p className="text-gray-600 text-sm">Streamline workflows and automate routine tasks to boost agent efficiency</p>
          </div>

          <div className="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Better Client Relations</h4>
            <p className="text-gray-600 text-sm">Enhance client satisfaction through organized communication and follow-ups</p>
          </div>

          <div className="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Data-Driven Insights</h4>
            <p className="text-gray-600 text-sm">Make informed decisions with comprehensive analytics and reporting tools</p>
          </div>
        </div>

        {/* Contact Information Card */}
        <div className="max-w-2xl mx-auto bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 shadow-2xl text-white">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="font-semibold">Phone</span>
                </div>
                <p className="text-blue-100">{contactPhone}</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="font-semibold">Email</span>
                </div>
                <p className="text-blue-100">{contactEmail}</p>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-white/20">
              <p className="text-blue-100 font-medium">{contactName}</p>
              <p className="text-blue-200 text-sm mt-1">Empowering agents, enhancing performance</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// LINE user routes
const LineApp = () => {
  return (
    <LineRoute>
      <LineLayout>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<LineDashboard />} />
            <Route path="/profile" element={<LineProfile />} />
            <Route path="/clients" element={<LineClients />} />
            <Route path="/policies" element={<LinePolicies />} />
            <Route path="/calendar" element={<LineCalendar />} />
            <Route path="/coaching" element={<LineCoaching />} />
            <Route path="/training" element={<LineTraining />} />
            <Route path="/performance" element={<LinePerformance />} />
            <Route path="/team" element={<LineTeam />} />
            <Route path="/notifications" element={<LineNotifications />} />
            <Route path="/tickets" element={<LineTickets />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </LineLayout>
    </LineRoute>
  );
};

// Admin routes
const AdminApp = () => {
  return (
    <AdminRoute>
      <AdminLayout>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<AdminDashboard />} />
            <Route path="/users" element={<AdminUsers />} />
            <Route path="/agents" element={<AdminAgents />} />
            <Route path="/clients" element={<AdminClients />} />
            <Route path="/policies" element={<AdminPolicies />} />
            <Route path="/calendar" element={<AdminCalendar />} />
            <Route path="/coaching" element={<AdminCoaching />} />
            <Route path="/performance" element={<AdminPerformance />} />
            <Route path="/reports" element={<AdminReports />} />
            <Route path="/settings" element={<AdminSettings />} />
            <Route path="/notifications" element={<AdminNotifications />} />
            <Route path="/notifications/settings" element={<AdminNotificationSettings />} />
            <Route path="/tickets" element={<AdminTickets />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </AdminLayout>
    </AdminRoute>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/liff" element={<LiffLogin />} />
          <Route path="/auth/one-time-login" element={<OneTimeLogin />} />
          <Route path="/auth/admin/login" element={<AdminLogin />} />
          <Route path="/auth/manager/login" element={<ManagerLogin />} />

          {/* Protected routes */}
          <Route path="/line/*" element={<LineApp />} />
          <Route path="/admin/*" element={<AdminApp />} />

          {/* Redirect any other routes to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
